// Story 6.4 (ADO-880, AC3) — friendly recovery surface for an EXPIRED or
// already-used set-password (activation) link.
//
// Rendered when the auth store enters `activation-error`. It explains the problem
// in plain language and offers a clear re-request action — with NO stack trace,
// NO raw Auth0 `error_description`, and NO generic "Authentication failed" text.
//
// ENUMERATION-SAFE (NFR15 / OWASP): submitting the email always shows the SAME
// generic confirmation, whether or not the account exists. The component never
// branches on the re-request outcome (the action resolves identically for any
// input), so account existence is never disclosed.
//
// a11y (NFR24–26): the heading receives focus on mount and on the confirmation
// transition (focus management), the email field is labelled, and the
// confirmation is announced via a persistently-mounted `role="status"` live
// region populated on the submitted transition (Story 6.26 / ADO-1205 — a node
// inserted already-populated is frequently skipped by screen readers). Story
// 6.26 also adds the in-form "Use a different email" correction affordance. The
// shared state machine lives in `useEmailRecoverySurface` (resolved once for
// this surface and its `ForgotPassword` clone).

import styles from './email-recovery-surface.module.css';
import { useEmailRecoverySurface } from './useEmailRecoverySurface';

const CONFIRMATION_MESSAGE = "If that email is registered, we've sent a new link.";

export interface ActivationLinkExpiredProps {
  /**
   * Re-request a fresh set-password link for the given email. Enumeration-safe:
   * resolves identically for any input. Null when no client/config exists yet —
   * the form still shows the generic confirmation (no account-existence signal).
   */
  onRequestNewLink: ((email: string) => Promise<void>) | null;
  /**
   * Return to the sign-in entry. Without it the `activation-error` state is a
   * dead end — only a full page reload escapes it. Optional so the component
   * stays usable in isolation; the shell wires it to re-run the bootstrap.
   */
  onReturnToSignIn?: () => void;
}

export function ActivationLinkExpired({
  onRequestNewLink,
  onReturnToSignIn,
}: ActivationLinkExpiredProps) {
  const {
    email,
    onEmailChange,
    submitted,
    announcement,
    headingRef,
    onSubmit,
    onUseDifferentEmail,
  } = useEmailRecoverySurface(onRequestNewLink, CONFIRMATION_MESSAGE);

  return (
    <>
      {/* Persistently-mounted live region (Story 6.26 / ADO-1205, AC1): always
          present, empty until the submitted transition, so the screen reader
          announces a text CHANGE on an existing region. Kept alongside — not
          instead of — the focus-to-heading behaviour below. */}
      <span className={styles.visuallyHidden} role="status">
        {announcement}
      </span>
      {submitted ? (
        <section aria-labelledby="activation-confirm-heading">
          <h2 id="activation-confirm-heading" tabIndex={-1} ref={headingRef}>
            Check your email
          </h2>
          {/* Enumeration-safe: identical message regardless of whether the email
              is registered (NFR15). Announced via the live region above, so this
              visible copy is a plain paragraph (Story 6.26 D-2 — a single status
              region avoids a double announcement / ambiguous role). */}
          <p>{CONFIRMATION_MESSAGE}</p>
          {/* Story 6.26 (ADO-1205, AC2) — light in-form correction of a mistyped
              email: resets the surface in place (no full reload) and refocuses
              the form heading; the entered value is preserved so it can be edited. */}
          <button type="button" onClick={onUseDifferentEmail}>
            Use a different email
          </button>
          {onReturnToSignIn && (
            <button type="button" onClick={onReturnToSignIn}>
              Return to sign in
            </button>
          )}
        </section>
      ) : (
        <section aria-labelledby="activation-expired-heading">
          <h2 id="activation-expired-heading" tabIndex={-1} ref={headingRef}>
            This link has expired
          </h2>
          <p>
            Your set-password link has expired or has already been used. Enter your email below and
            we&apos;ll send you a new one.
          </p>
          <form onSubmit={onSubmit}>
            <label htmlFor="activation-email">Email address</label>
            <input
              id="activation-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
            />
            <button type="submit">Email me a new link</button>
          </form>
        </section>
      )}
    </>
  );
}

export default ActivationLinkExpired;
